---
id: apps-micro-services-world-map-search-doc
title: World Map Search Service
owner: Tashi
status: Active
lastReviewed: 2026-01-29
visibility: internal
tags:
  - microservice
  - search
  - embeddings
  - qdrant
  - llm
---

## Product Overview

**Who it serves:** Provides a semantic query layer for internal product flows that need to map a user’s natural language search into standardized role, skill, and industry labels, plus parsed organization signals.

**Primary journey:** Accepts a search input, either parses a natural-language query into structured roles/skills/industries/organizations, resolves a single job title directly to ESCO role labels, or extracts a role from a broader query before performing ESCO role lookup. Keyword groups preserve AND/OR intent so downstream consumers can apply filters or matching logic consistently.

**Business rules:** Only categories explicitly mentioned in the query are returned. When nothing relevant is found, the service returns an empty response rather than partial guesses.

**Out of scope:** Does not retrieve candidate or company data, does not rank beyond top-k per keyword, and does not persist or audit user queries.

## Technical Overview

**Architecture flow:** The FastAPI entrypoint in `main.py` exposes `/search/parse-query`, `/search/get-esco-role`, and `/search/llm-get-esco-role` and composes three runtime services: LLM parsing (`app/llm.py`), LLM role extraction (`app/llm_esco_role_search.py`), embeddings (`app/embed.py`), and vector search (`app/qdrant.py`). The request paths are parse → embed → Qdrant search, direct embed → Qdrant role search, or LLM role extraction → embed → Qdrant role search, with category searches executed in parallel by `SearchPipeline` in `app/search_parser.py`.

**Key modules:** `app/models.py` defines the request/response shapes, keyword grouping, and parsed query contracts. `app/config.py` centralizes collection names, embedding model choices, and model provider settings used across the pipeline.

**Integrations and side effects:** The service calls external LLM and embedding providers via LangChain and the OpenAI SDK, and queries a Qdrant cluster for vector matches. Responses map Qdrant payload fields into normalized labels that downstream clients can display or filter on.

**Constraints and risks:** Embedding model settings must align with how the Qdrant collections were indexed, and the service’s correctness depends on the LLM parser adhering to the structured output schema.

## Changelog

- 2026-01-29: Initial documentation.
